import { Col, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';

import { Permission, PermissionType } from '../types/permission';

interface UserPermissionProps {
  permissions: PermissionType[];
  userPermissions: PermissionType[];
  showAdminPermission: boolean;
  onChange: (permission: PermissionType, value: boolean) => void;
  isFacilitator?: boolean;
  isAdmin?: boolean;
}

const UserPermission = (props: UserPermissionProps) => {
  const { pathname } = useLocation();
  const isFacilitator =
    props.isFacilitator !== undefined
      ? props.isFacilitator
      : pathname.includes('facilitators');
  const isAdmin =
    props.isAdmin !== undefined
      ? props.isAdmin
      : pathname.includes('administration');

  const childPendingApprovalRole = (
    Object.keys(Permission) as PermissionType[]
  ).filter(
    (permission, index) =>
      permission.split('.').length > 2 &&
      permission.split('.')[1] === 'PendingApproval' &&
      index > 0
  );

  const permissionKeyList = Object.keys(Permission) as PermissionType[];

  const childAdminRoles = (Object.keys(Permission) as PermissionType[]).filter(
    (permission, index) =>
      permission.split('.').length > 1 &&
      permission.split('.').length < 3 &&
      permission.split('.')[0] === Permission.Admin &&
      index > 0
  );

  const childAppPermission = (
    Object.keys(Permission) as PermissionType[]
  ).filter(
    (permission, index) =>
      permission.split('.')[0] === 'App' &&
      permission.split('.').length > 1 &&
      index > 0
  );

  const hasSpecialCaseAccess =
    props.userPermissions?.some(
      (p) => p === 'App' || p.startsWith('App.')
    ) ?? false;

  const setRoleToDisable = (permission?: string) => {
    if (isFacilitator) {
      if (
        permission === 'Facilitator' ||
        permission?.startsWith('Facilitator.')
      ) {
        return false;
      }
      if (permission === 'App' || permission?.startsWith('App.')) {
        return !isAdmin;
      }
    }
    let result = props.permissions?.find((role) => role.includes(permission!));
    if (result) return false;
    return true;
  };

  const OnChangeHandler = (e: CheckboxChangeEvent) => {
    if (isFacilitator && !isAdmin) {
      const name = e.target.name as string;
      if (name === 'App' || name.startsWith('App.')) {
        return;
      }
    }
    props.onChange(e.target.name as PermissionType, e.value);
  };

  return (
    <div className="ml-10">
      {permissionKeyList
        .filter(
          (fp) =>
            fp.split('.')[0] !== Permission.Admin && fp.split('.')[0] !== 'App'
        )
        .filter((fp) =>
          isFacilitator
            ? [
                'Facilitator',
                'Facilitator.Portal',
                'Facilitator.Spark',
                'Facilitator.Assessment'
              ].includes(fp)
            : fp
        )
        .map((permission, index) => {
          const marginLeft = permission.split('.').length > 1 ? 'ml-5' : '';
          const marginTop = index > 0 ? 'mt-2' : '';
          return (
            <Row className={`${marginTop} ${marginLeft}`} key={index}>
              <Col size={'md-4'}>
                <Checkbox
                  name={permission}
                  label={Permission[permission]}
                  size={'large'}
                  value={props.userPermissions?.includes(permission)}
                  onChange={OnChangeHandler}
                  disabled={setRoleToDisable(permission)}
                />
              </Col>
            </Row>
          );
        })}

      {/* Special Case Activities (App role) */}
      {(isFacilitator || props.showAdminPermission) && (
        <>
          <Row className={'mt-2'}>
            <Col size={'md-4'}>
              <Checkbox
                name={'App'}
                label={Permission.App}
                size={'large'}
                value={props.userPermissions?.includes('App')}
                onChange={OnChangeHandler}
                disabled={setRoleToDisable('App')}
              />
            </Col>
          </Row>

          {/* Child application role */}
          {childAppPermission.map((permission) => (
            <Row className={'mt-2 ml-5'} key={permission}>
              <Col size={'md-4'}>
                <Checkbox
                  name={permission}
                  label={Permission[permission]}
                  size={'large'}
                  value={props.userPermissions?.includes(permission)}
                  onChange={OnChangeHandler}
                  disabled={setRoleToDisable(permission)}
                />
              </Col>
            </Row>
          ))}
        </>
      )}

      {/* Admin role - only for standard users when showAdminPermission is true */}
      {!isFacilitator && props.showAdminPermission && (
        <>
          <Row className={'mt-2'}>
            <Col size={'md-4'}>
              <Checkbox
                name={Permission.Admin}
                label={Permission.Admin}
                size={'large'}
                value={props.userPermissions?.includes(Permission.Admin)}
                onChange={OnChangeHandler}
                disabled={setRoleToDisable(Permission.Admin)}
              />
            </Col>
          </Row>

          {/* Admin - Child Roles */}
          {childAdminRoles
            .filter(
              (child) =>
                child !== 'Admin.SupportTickets' &&
                child !== 'Admin.ActiveEvents' &&
                child !== 'Admin.PendingApproval'
            )
            .map((permission, index) => (
              <Row className={'mt-2 ml-5'} key={`child_${index}`}>
                <Col size={'md-4'}>
                  <Checkbox
                    name={permission}
                    label={Permission[permission]}
                    size={'large'}
                    value={props.userPermissions?.includes(permission)}
                    onChange={OnChangeHandler}
                    disabled={setRoleToDisable(permission)}
                  />
                </Col>
              </Row>
            ))}

          {childAdminRoles
            .filter((child) => child === 'Admin.PendingApproval')
            .map((permission, index) => (
              <Row className={'mt-2 ml-5'} key={`child_${index}`}>
                <Col size={'md-4'}>
                  <Checkbox
                    name={permission}
                    label={Permission[permission]}
                    size={'large'}
                    value={props.userPermissions?.includes(permission)}
                    onChange={OnChangeHandler}
                    disabled={setRoleToDisable(permission)}
                  />
                </Col>
              </Row>
            ))}

          {/* Admin Pending Approval - Child Roles */}
          {childPendingApprovalRole.map((permission, index) => (
            <Row className={'mt-2 ml-12'} key={`child_child_${index}`}>
              <Col size={'md-4'}>
                <Checkbox
                  name={permission}
                  label={Permission[permission]}
                  size={'large'}
                  value={props.userPermissions?.includes(permission)}
                  onChange={OnChangeHandler}
                  disabled={setRoleToDisable(permission)}
                />
              </Col>
            </Row>
          ))}

          {childAdminRoles
            .filter((child) => child === 'Admin.SupportTickets')
            .map((permission, index) => (
              <Row className={'mt-2 ml-5'} key={`child_${index}`}>
                <Col size={'md-4'}>
                  <Checkbox
                    name={permission}
                    label={Permission[permission]}
                    size={'large'}
                    value={props.userPermissions?.includes(permission)}
                    onChange={OnChangeHandler}
                    disabled={setRoleToDisable(permission)}
                  />
                </Col>
              </Row>
            ))}

          {childAdminRoles
            .filter((child) => child === 'Admin.ActiveEvents')
            .map((permission, index) => (
              <Row className={'mt-2 ml-5'} key={`child_${index}`}>
                <Col size={'md-4'}>
                  <Checkbox
                    name={permission}
                    label={Permission[permission]}
                    size={'large'}
                    value={props.userPermissions?.includes(permission)}
                    onChange={OnChangeHandler}
                    disabled={setRoleToDisable(permission)}
                  />
                </Col>
              </Row>
            ))}
        </>
      )}
    </div>
  );
};

export default UserPermission;
